/**
 * 塩基の 1 byte 表現。`.gproj` の `sequences/**.bin` と同じ符号（§3.1）。
 * A=0 / C=1 / G=2 / T=3（U も T）/ N=4。IUPAC 曖昧塩基は N に丸める。
 */

export const CODE_A = 0;
export const CODE_C = 1;
export const CODE_G = 2;
export const CODE_T = 3;
export const CODE_N = 4;

export const NUCLEOTIDE_CODE_COUNT = 5;

/** コード → 文字 */
export const CODE_TO_CHAR = 'ACGTN' as const;

/** `byteToCode` の戻り値のうち塩基ではないもの */
export const BYTE_IGNORE = 254;
export const BYTE_FOREIGN = 255;

const IUPAC_AMBIGUOUS = 'RYSWKMBDHVN';
/** タンパク質にしか出ない文字（I17 のタンパク質判定に使う） */
const PROTEIN_ONLY = 'EFILPQZJOX';

function buildByteTable(): Uint8Array {
  const table = new Uint8Array(256).fill(BYTE_FOREIGN);

  const assign = (char: string, code: number) => {
    table[char.charCodeAt(0)] = code;
    table[char.toLowerCase().charCodeAt(0)] = code;
  };

  assign('A', CODE_A);
  assign('C', CODE_C);
  assign('G', CODE_G);
  assign('T', CODE_T);
  assign('U', CODE_T);
  for (const char of IUPAC_AMBIGUOUS) assign(char, CODE_N);

  // 配列本文で無視する文字（空白・改行・行番号・ギャップ・終止記号）
  for (const char of ' \t\r\n\v\f-.*0123456789') {
    table[char.charCodeAt(0)] = BYTE_IGNORE;
  }

  for (const char of PROTEIN_ONLY) {
    table[char.charCodeAt(0)] = BYTE_FOREIGN;
    table[char.toLowerCase().charCodeAt(0)] = BYTE_FOREIGN;
  }

  return table;
}

/** ASCII バイト → 塩基コード（`BYTE_IGNORE` / `BYTE_FOREIGN` を含む） */
export const BYTE_TO_CODE = buildByteTable();

const COMPLEMENT_CODE = new Uint8Array([CODE_T, CODE_G, CODE_C, CODE_A, CODE_N]);

/** 1 文字を塩基コードへ。塩基でなければ `null`。 */
export function encodeBase(char: string): number | null {
  if (char.length !== 1) return null;
  const code = BYTE_TO_CODE[char.charCodeAt(0) & 0xff];
  return code < NUCLEOTIDE_CODE_COUNT ? code : null;
}

/** 塩基コード列 → 文字列 */
export function decodeCodes(codes: Uint8Array, start = 0, end = codes.length): string {
  const limit = Math.min(end, codes.length);
  let out = '';
  // 文字列連結の再確保を避けるため 8 kb ずつ組み立てる
  const BATCH = 8192;
  for (let base = Math.max(0, start); base < limit; base += BATCH) {
    const stop = Math.min(base + BATCH, limit);
    const parts = new Array<string>(stop - base);
    for (let i = base; i < stop; i += 1) {
      parts[i - base] = CODE_TO_CHAR[codes[i] < NUCLEOTIDE_CODE_COUNT ? codes[i] : CODE_N];
    }
    out += parts.join('');
  }
  return out;
}

/** 文字列 → 塩基コード列。塩基以外は落とす。 */
export function encodeSequence(text: string): Uint8Array {
  const out = new Uint8Array(text.length);
  let length = 0;
  for (let i = 0; i < text.length; i += 1) {
    const code = BYTE_TO_CODE[text.charCodeAt(i) & 0xff];
    if (code < NUCLEOTIDE_CODE_COUNT) {
      out[length] = code;
      length += 1;
    }
  }
  return out.subarray(0, length);
}

/** 相補塩基コード */
export function complementCode(code: number): number {
  return code < NUCLEOTIDE_CODE_COUNT ? COMPLEMENT_CODE[code] : CODE_N;
}

/** 相補鎖（順序はそのまま）。2 本鎖表示の下段に使う（§0.3.4）。 */
export function complementCodes(codes: Uint8Array): Uint8Array {
  const out = new Uint8Array(codes.length);
  for (let i = 0; i < codes.length; i += 1) out[i] = complementCode(codes[i]);
  return out;
}

/** 逆相補 */
export function reverseComplementCodes(codes: Uint8Array): Uint8Array {
  const out = new Uint8Array(codes.length);
  for (let i = 0; i < codes.length; i += 1) {
    out[codes.length - 1 - i] = complementCode(codes[i]);
  }
  return out;
}

/** GC 数（N は分母から外さない。GC% は呼び出し側で決める） */
export function countGc(codes: Uint8Array, start = 0, end = codes.length): number {
  let count = 0;
  const limit = Math.min(end, codes.length);
  for (let i = Math.max(0, start); i < limit; i += 1) {
    if (codes[i] === CODE_C || codes[i] === CODE_G) count += 1;
  }
  return count;
}
